/** One Server-Sent Event: its type ("message" when none is given), data and id field. */
export interface SseEvent {
  event: string;
  data: string;
  id: string | null;
}

/**
 * Reads a Server-Sent Events stream as it arrives, in chunks split anywhere (even mid-line), and
 * reports each complete event. Follows the HTML spec's parsing rules: comments (": ping") are
 * skipped, `data` lines are joined with newlines, and `retry` sets the reconnection delay.
 */
export function createSseParser(handlers: { onEvent: (event: SseEvent) => void; onRetry?: (ms: number) => void }) {
  let buffer = '';
  let data: string[] = [];
  let event = '';
  let id: string | null = null;

  const dispatch = () => {
    if (data.length) handlers.onEvent({ event: event || 'message', data: data.join('\n'), id });
    data = [];
    event = '';
    id = null;
  };

  const readLine = (line: string) => {
    if (line === '') return dispatch();
    if (line.startsWith(':')) return;
    const colon = line.indexOf(':');
    const field = colon === -1 ? line : line.slice(0, colon);
    let value = colon === -1 ? '' : line.slice(colon + 1);
    if (value.startsWith(' ')) value = value.slice(1);
    if (field === 'data') data.push(value);
    else if (field === 'event') event = value;
    else if (field === 'id' && !value.includes('\0')) id = value;
    else if (field === 'retry' && /^\d+$/.test(value)) handlers.onRetry?.(Number(value));
  };

  return {
    push(chunk: string) {
      buffer += chunk;
      // A trailing \r may be the first half of \r\n: wait for the next chunk to find out.
      const lines = buffer.split(/\r\n|\r(?!$)|\n/);
      buffer = lines.pop() ?? '';
      for (const line of lines) readLine(line);
    },
  };
}
